import { useEffect, useRef } from 'react';
import { useGlobalContext } from '~/context/GlobalContext';

function MediaContainer() {
  const { context } = useGlobalContext(); // Możesz to usunąć, jeśli nie używasz 'context'

  // Referencje dla różnych dźwięków
  const refAudioMessage = useRef<HTMLAudioElement>(null);
  const refAudioInvite = useRef<HTMLAudioElement>(null);
  const refAudioCallingComing = useRef<HTMLAudioElement>(null);
  const refAudioRingingOut = useRef<HTMLAudioElement>(null);

  // Inicjalizacja stanu dźwięku
  const isSoundEnabled = useRef(
    localStorage.getItem('sound') !== null
      ? localStorage.getItem('sound') === 'true'
      : true
  );

  // --- NOWE: Inicjalizacja stanu wibracji i referencja dla pętli ---
  const isVibrationEnabled = useRef(
    localStorage.getItem('vibration') !== null
      ? localStorage.getItem('vibration') === 'true'
      : true
  );
  const vibrationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- LOGIKA WIBRACJI ---
  const triggerVibration = (pattern: number | number[]) => {
    if (isVibrationEnabled.current && 'vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const startIncomingCallVibration = () => {
    if (!isVibrationEnabled.current || !('vibrate' in navigator)) return;
    
    // Pierwsza wibracja od razu (1 sekunda wibruje, 1 sekunda pauzy)
    navigator.vibrate([1000, 1000]);
    
    // Powtarzaj wibrację co 2 sekundy (dopóki dzwoni)
    if (!vibrationIntervalRef.current) {
      vibrationIntervalRef.current = setInterval(() => {
        navigator.vibrate([1000, 1000]);
      }, 2000);
    }
  };

  const stopVibration = () => {
    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
      vibrationIntervalRef.current = null;
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(0); // Wymusza zatrzymanie wszelkich wibracji
    }
  };

  // --- LOGIKA AUDIO ---
  const playSound = (audioRef: React.RefObject<HTMLAudioElement>) => {
    if (!isSoundEnabled.current || !audioRef.current) return;
    audioRef.current.volume = 1.0;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((error) => {
      console.warn('Odtwarzanie dźwięku zablokowane:', error);
    });
  };

  const audioNotificationInvite = () => {
    playSound(refAudioInvite);
    triggerVibration([200, 100, 200]); // Wibracja dla zaproszenia (podwójna)
  };

  const audioNotification = () => {
    playSound(refAudioMessage);
    triggerVibration(200); // Zwykła wiadomość (pojedyncza krótka)
  };

  const audioCallingComing = () => {
    playSound(refAudioCallingComing);
    startIncomingCallVibration(); // Start pętli wibracji dla połączenia
  };

  const audioRingingOut = () => {
    playSound(refAudioRingingOut);
    // Zwykle przy wychodzących połączeniach telefon nie wibruje, więc tu tylko dźwięk
  };

  const stopCallingSound = () => {
    if (refAudioCallingComing.current) {
      refAudioCallingComing.current.pause();
      refAudioCallingComing.current.currentTime = 0;
    }
    if (refAudioRingingOut.current) {
      refAudioRingingOut.current.pause();
      refAudioRingingOut.current.currentTime = 0;
    }
    stopVibration(); // Zatrzymanie wibracji, gdy połączenie się kończy
  };

  const stopIncomingCallSound = () => {
    if (refAudioCallingComing.current) {
      refAudioCallingComing.current.pause();
      refAudioCallingComing.current.currentTime = 0;
    }
    stopVibration();
  };

  // Zaktualizowany event, który obsługuje zarówno wyciszanie dźwięku, jak i wibracji
  const eventSettings = (e: any) => {
    if (!e.detail) return;

    // Aktualizacja dźwięku
    if (typeof e.detail.sound_chat !== 'undefined') {
      const newSoundStatus = e.detail.sound_chat;
      isSoundEnabled.current = newSoundStatus;
      localStorage.setItem('sound', String(newSoundStatus));
      if (!newSoundStatus) stopCallingSound();
    }

    // Aktualizacja wibracji
    if (typeof e.detail.vibration_chat !== 'undefined') {
      const newVibrationStatus = e.detail.vibration_chat;
      isVibrationEnabled.current = newVibrationStatus;
      localStorage.setItem('vibration', String(newVibrationStatus));
      if (!newVibrationStatus) stopVibration();
    }
  };

  useEffect(() => {
    window.addEventListener('audioNotification', audioNotification);
    window.addEventListener('audioNotificationInvite', audioNotificationInvite);
    
    // Używamy zaktualizowanego nasłuchiwacza na ustawienia
    window.addEventListener('eventAudio', eventSettings); 

    window.addEventListener('startCallingComing', audioCallingComing);
    window.addEventListener('startRingingOut', audioRingingOut);

    window.addEventListener('cancelCall', stopCallingSound);
    window.addEventListener('userIsBusy', stopCallingSound);
    window.addEventListener('stopCallingSound', stopCallingSound);
    window.addEventListener('stopIncomingCallSound', stopIncomingCallSound);

    return () => {
      window.removeEventListener('audioNotification', audioNotification);
      window.removeEventListener('audioNotificationInvite', audioNotificationInvite);
      window.removeEventListener('eventAudio', eventSettings);
      window.removeEventListener('startCallingComing', audioCallingComing);
      window.removeEventListener('startRingingOut', audioRingingOut);

      window.removeEventListener('cancelCall', stopCallingSound);
      window.removeEventListener('userIsBusy', stopCallingSound);
      window.removeEventListener('stopCallingSound', stopCallingSound);
      window.removeEventListener('stopIncomingCallSound', stopIncomingCallSound);
      
      // Czyszczenie interwału, jeśli komponent zostanie odmontowany podczas dzwonienia
      stopVibration();
    };
  }, []);

  return (
    <div data-component="MediaContainer" style={{ display: 'none' }}>
      <audio ref={refAudioMessage} preload="auto">
        <source src="/audio/message.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={refAudioInvite} preload="auto">
        <source src="/audio/invitation.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={refAudioCallingComing} preload="auto" loop>
        <source src="/audio/calling.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={refAudioRingingOut} preload="auto" loop>
        <source src="/audio/ringing.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
}

export default MediaContainer;